/**
 * Pure helpers for the checkout page's campus scope — the "All campuses"
 * tab on `/orders/school/checkout` (multi-campus admins). Mirrors
 * `cart.ts` / `funds.ts`: no React, no `@/api` runtime imports, so the
 * page and the unit tests share the same logic.
 *
 * The cart store buckets lines per campus (`linesByLocation`), and the
 * submit pipeline runs per campus (`checkoutSchoolCart` binds one
 * campus's account + service location). "All campuses" is therefore a
 * VIEW + SUBMIT SCOPE over those per-campus carts, not a merged cart:
 *
 *   - `buildScopedCheckoutPlans` keeps one per-campus checkout plan per
 *     campus that has cart lines (tab order preserved);
 *   - `groupScopedCheckoutByDay` rolls the scope up into the review
 *     page's Date → Campus → Mealtime hierarchy;
 *   - `scopedEstimatedTaxCents` sums the per-campus invoice estimates
 *     (tax rates are campus-scoped);
 *   - `scopedFundsGates` evaluates the insufficient-funds gate per
 *     BILLING ACCOUNT: account resolution is campus-first with an
 *     org-wide fallback (`resolveBillingAccountId`), so two campuses may
 *     share one account — a scope submit debits that account once per
 *     campus, and gating each campus against the full balance would pass
 *     individually and still 402 midway through the loop.
 */

import type { CheckoutGroup, SchoolCartLine } from '@/lib/school-orders/cart'
import { buildCheckoutPlan, mealQty, subtotalCents, totalQty } from '@/lib/school-orders/cart'
import { type SchoolFundsAccountView, type SchoolFundsGate, schoolFundsGate } from '@/lib/school-orders/funds'
import { schoolOrderEstimatedTaxCents } from '@/lib/school-orders/tax'

/** One campus's cart slice as fed into the scope helpers. */
export interface CampusCartEntry<S extends { id: string }> {
  school: S
  lines: SchoolCartLine[]
}

/** One campus's checkout plan within the scope — its groups plus the raw lines the money math reads. */
export interface CampusCheckoutPlan<S extends { id: string }> {
  school: S
  lines: SchoolCartLine[]
  groups: CheckoutGroup[]
}

/**
 * The per-campus checkout plans for a scope: one entry per campus whose
 * cart holds at least one submittable line, in the given (tab) order.
 * Campuses with empty carts are dropped — the All view omits them.
 */
export function buildScopedCheckoutPlans<S extends { id: string }>(
  entries: readonly CampusCartEntry<S>[],
): CampusCheckoutPlan<S>[] {
  const plans: CampusCheckoutPlan<S>[] = []
  for (const entry of entries) {
    const groups = buildCheckoutPlan(entry.lines)
    if (groups.length > 0) {
      plans.push({ school: entry.school, lines: entry.lines, groups })
    }
  }
  return plans
}

/** One campus's mealtime groups under a day section of the All view. */
export interface ScopedCheckoutDayCampus<S extends { id: string }> {
  school: S
  /** The campus's `(date, mealtime)` groups for this day, canonical mealtime order. */
  mealtimes: CheckoutGroup[]
}

/** A day section of the All view: Date → Campus → Mealtime. */
export interface ScopedCheckoutDay<S extends { id: string }> {
  date: string
  campuses: ScopedCheckoutDayCampus<S>[]
}

/**
 * Roll the scope's per-campus plans up into the review hierarchy: days
 * ascending; within a day, campuses in scope (tab) order, keeping only
 * campuses with lines on that day; within a campus, mealtimes keep the
 * canonical order `buildCheckoutPlan` sorted them into.
 */
export function groupScopedCheckoutByDay<S extends { id: string }>(
  plans: readonly CampusCheckoutPlan<S>[],
): ScopedCheckoutDay<S>[] {
  const dates = new Set<string>()
  for (const plan of plans) {
    for (const group of plan.groups) {
      dates.add(group.date)
    }
  }
  return [...dates].sort().map((date) => ({
    date,
    campuses: plans
      .map((plan) => ({ school: plan.school, mealtimes: plan.groups.filter((g) => g.date === date) }))
      .filter((campus) => campus.mealtimes.length > 0),
  }))
}

/**
 * The scope's estimated-invoice tax row: per-campus estimates summed —
 * tax inputs (`campusTaxRateBps`, `orgTaxExempt`) are campus-scoped, so
 * one blended rate would be wrong whenever campuses differ.
 *
 * Null contract mirrors the single-campus row, extended honestly to a
 * scope: a campus with PRICED lines whose estimate is unknowable (rate
 * unconfigured, org not exempt) makes the WHOLE scope estimate null —
 * showing the sum of the known campuses would understate the invoice.
 * Campuses with no priced lines contribute nothing either way, and a
 * scope with no priced lines at all is null (mirrors `subtotalCents`).
 */
export function scopedEstimatedTaxCents(
  entries: readonly {
    lines: SchoolCartLine[]
    tax: { campusTaxRateBps: number | null | undefined; orgTaxExempt?: boolean }
  }[],
): number | null {
  let total = 0
  let known = false
  for (const entry of entries) {
    const estimate = schoolOrderEstimatedTaxCents(entry.lines, entry.tax)
    if (estimate === null) {
      if (subtotalCents(entry.lines) !== null) {
        return null
      }
      continue
    }
    total += estimate
    known = true
  }
  return known ? total : null
}

/** One campus's slice of the cross-campus cart summary. */
export interface CrossCampusCartCampus<S extends { id: string }> {
  school: S
  /** Meal + à-la-carte count (derived system-milk lines excluded). */
  qty: number
}

/** The union-cart summary the rail / mobile bar render across every campus. */
export interface CrossCampusCartSummary<S extends { id: string }> {
  /** Meal + à-la-carte count across the scope (milk-exclusion rule). */
  qty: number
  /** ALL-lines emptiness — a cart can't hide lines behind "empty". */
  empty: boolean
  subtotalCents: number | null
  estimatedTaxCents: number | null
  /** Tax-inclusive when subtotal is known; mirrors the single-campus rail. */
  totalCents: number | null
  currency: string
  /** Campuses holding at least one line, in scope (tab) order. */
  campuses: CrossCampusCartCampus<S>[]
}

/**
 * The whole cart across every campus, with the exact single-campus rail
 * arithmetic extended to the scope: counts and money treat the union of
 * lines as one cart (`subtotalCents` still sums only priced lines and
 * goes null when NONE are priced), while tax stays per-campus-rate via
 * `scopedEstimatedTaxCents` — so the rail's Total equals what the
 * All-campuses checkout summary will show.
 */
export function crossCampusCartSummary<
  S extends { id: string; tax: { campusTaxRateBps: number | null | undefined; orgTaxExempt?: boolean } },
>(entries: readonly CampusCartEntry<S>[]): CrossCampusCartSummary<S> {
  const allLines = entries.flatMap((entry) => entry.lines)
  const subtotal = subtotalCents(allLines)
  const estimatedTax = scopedEstimatedTaxCents(entries.map((entry) => ({ lines: entry.lines, tax: entry.school.tax })))
  return {
    qty: mealQty(allLines),
    empty: totalQty(allLines) === 0,
    subtotalCents: subtotal,
    estimatedTaxCents: estimatedTax,
    totalCents: subtotal !== null ? subtotal + (estimatedTax ?? 0) : null,
    currency: allLines.find((l) => l.currency)?.currency ?? 'USD',
    campuses: entries
      .filter((entry) => totalQty(entry.lines) > 0)
      .map((entry) => ({ school: entry.school, qty: mealQty(entry.lines) })),
  }
}

/** The account view the scoped gate reads — the campus's balance payload slice. */
export interface ScopedFundsAccountView extends SchoolFundsAccountView {
  accountId: string
  currency: string
}

/** One campus's inputs to the scoped funds gate. */
export interface CampusFundsInput {
  locationId: string
  /** Null while the campus's balance read is loading or errored — that campus fails OPEN. */
  account: ScopedFundsAccountView | null
  /** Null when no line carries a price — fails OPEN (such a cart 422s server-side anyway). */
  expectedDebitCents: number | null
}

/** The gate decision for one billing account, with the campuses it covers. */
export interface ScopedAccountGate {
  accountId: string
  currency: string
  /** The campuses debiting this account in scope (priced carts only), input order. */
  locationIds: string[]
  gate: SchoolFundsGate
}

/**
 * Evaluate the insufficient-funds gate for a scope, per billing ACCOUNT:
 * campuses resolving to the same account have their expected debits
 * SUMMED before the ledger rule runs, because a scope submit debits that
 * account once per campus — the shortfall shown is then the minimum
 * top-up that makes the whole in-scope order placeable. Campuses with
 * distinct accounts degenerate to today's per-campus gate.
 *
 * Fail-open inputs (null account or null debit) are excluded entirely —
 * they never gate and never inflate another campus's sum. The account
 * fields are read off the first input per account; callers refresh every
 * scope balance read together so the snapshots agree.
 */
export function scopedFundsGates(inputs: readonly CampusFundsInput[]): ScopedAccountGate[] {
  const byAccount = new Map<string, { account: ScopedFundsAccountView; locationIds: string[]; debitCents: number }>()
  for (const input of inputs) {
    if (!input.account || input.expectedDebitCents === null) {
      continue
    }
    const group = byAccount.get(input.account.accountId)
    if (group) {
      group.locationIds.push(input.locationId)
      group.debitCents += input.expectedDebitCents
    } else {
      byAccount.set(input.account.accountId, {
        account: input.account,
        locationIds: [input.locationId],
        debitCents: input.expectedDebitCents,
      })
    }
  }
  return [...byAccount.values()].map((group) => ({
    accountId: group.account.accountId,
    currency: group.account.currency,
    locationIds: group.locationIds,
    gate: schoolFundsGate(group.account, group.debitCents),
  }))
}

/** The campuses excluded from a scope submit — every campus on a gated account. */
export function gatedLocationIdSet(gates: readonly ScopedAccountGate[]): Set<string> {
  const ids = new Set<string>()
  for (const accountGate of gates) {
    if (accountGate.gate.gated) {
      for (const id of accountGate.locationIds) {
        ids.add(id)
      }
    }
  }
  return ids
}
